import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Truck, Wrench, CalendarBlank, Warning, CaretLeft, QrCode } from "@phosphor-icons/react";
import EquipmentQRDialog from "@/components/EquipmentQRDialog";

export default function EquipmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/equipment/${id}/history`); setData(r.data); }
      catch { setData({ error: true }); }
    })();
  }, [id]);

  if (!data) return <div className="p-6 text-white/40 text-sm">Chargement…</div>;
  if (data.error) return <div className="p-6 text-red-400 text-sm">Équipement introuvable</div>;

  const eq = data.equipment;
  const IconC = eq.kind === "vehicle" ? Truck : Wrench;
  const color = eq.kind === "vehicle" ? "#005CFF" : "#F59E0B";

  return (
    <div className="p-4 space-y-4" data-testid="equipment-detail-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white">
        <CaretLeft size={14} /> Retour
      </button>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 flex items-center gap-3">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: color + "22", color }}>
          <IconC size={26} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-lg truncate">{eq.name}</div>
          <div className="text-xs text-white/50 truncate">
            {[eq.brand, eq.model, eq.plate || eq.serial].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <button onClick={() => setQrOpen(true)} className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10" data-testid="equipment-show-qr">
          <QrCode size={18} />
        </button>
      </div>

      {eq.notes && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Notes</div>
          <div className="text-sm whitespace-pre-wrap">{eq.notes}</div>
        </div>
      )}

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3">
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Emplacement</div>
        <div className="text-sm">
          {eq.location_kind === "vehicle"
            ? <span>Dans le véhicule <b className="text-[#7CB2FF]">{eq.location_vehicle_id?.slice(0, 8) || "—"}</b></span>
            : <span>À l&apos;atelier</span>}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Échéances de maintenance</div>
        {(data.due_dates || []).length === 0 && <div className="text-xs text-white/40">Aucune échéance enregistrée.</div>}
        <div className="space-y-1.5">
          {(data.due_dates || []).map((d) => {
            const due = new Date(d.due_date + "T00:00:00");
            const days = Math.round((due - new Date()) / 86400000);
            const overdue = days < 0;
            return (
              <div key={d.id} className={`flex items-center gap-2 p-2.5 rounded-lg border ${overdue ? "bg-red-500/10 border-red-500/30" : "bg-white/[0.03] border-white/10"}`}>
                <CalendarBlank size={14} className="text-white/40" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{d.label || d.kind}</div>
                  <div className="text-[10px] text-white/40">{d.due_date}</div>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${overdue ? "bg-red-500 text-white" : "bg-white/10 text-white/70"}`}>
                  {overdue ? `+${Math.abs(days)}j` : `J-${days}`}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <EquipmentQRDialog open={qrOpen} onClose={() => setQrOpen(false)} equipment={eq} />
    </div>
  );
}
